import React, { useState, useEffect } from "react";
import configs, { getParameterByName } from "../../Constants";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Chip,
  Stack,
  Typography,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import LockClockIcon from "@mui/icons-material/LockClock";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

function PlanExpiryDialog({ userData, themeColor = "#1976d2", navigate }) {
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  let token = sessionStorage.getItem("token");
  let merchantData = sessionStorage.getItem("merchantData")
    ? JSON.parse(sessionStorage.getItem("merchantData"))
    : null;
  const merchantInParam = getParameterByName("merchantCode");
  const merchCode =
    merchantData && merchantData.merchantCode
      ? merchantData.merchantCode
      : merchantInParam;
  const paramPath = merchantInParam ? "merchantCode=" + merchCode : "";
  const memberPath = `/members?${paramPath}`;

  const toLocalEndOfDay = (iso) => {
    if (!iso) return null;
    // Accept "YYYY-MM-DD" or full ISO
    const base = iso.length <= 10 ? `${iso}T23:59:59` : iso;
    const d = new Date(base);
    if (Number.isNaN(d.getTime())) return null;
    // Force to end of day local
    d.setHours(23, 59, 59, 999);
    return d;
  };

  useEffect(() => {
    const validUntil =
      userData?.validUntil || userData?.plan?.validUntil || null;

    const expiry = toLocalEndOfDay(validUntil);
    if (!expiry) return;

    const onMembersPage = window.location.pathname.startsWith("/members");
    if (onMembersPage) {
      setOpen(false);
      return;
    }

    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    setDaysLeft(diffDays);
    const expired = diffDays < 0;
    setIsExpired(expired);

    const remindKey = "planReminderDismissedUntil";
    const dismissedUntil = sessionStorage.getItem(remindKey);
    const dismissed = dismissedUntil ? new Date(dismissedUntil) : null;

    if (expired) {
      setOpen(true);
    } else if (diffDays <= 10) {
      const todayKey = new Date().toDateString();
      if (!dismissed || dismissed.toDateString() !== todayKey) {
        setOpen(true);
      }
    }
  }, [userData]);

  const handleCloseForToday = () => {
    // Only allow closing if not expired
    if (isExpired) return;
    setOpen(false);
  };

  const handleRemindTomorrow = () => {
    if (isExpired) return;
    const tmr = new Date();
    // Store today's date string to suppress for the rest of today
    sessionStorage.setItem("planReminderDismissedUntil", tmr.toISOString());
    setOpen(false);
  };

  const handleUpdatePlan = () => {
    sessionStorage.setItem(
      "planReminderDismissedUntil",
      new Date().toISOString()
    );

    setOpen(false);

    setTimeout(() => {
      navigate(memberPath);
    }, 0);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userData");
    localStorage.removeItem("userData");
    sessionStorage.removeItem("selectedBranchId");
    sessionStorage.removeItem("merchantData");
    sessionStorage.removeItem('branch-merchants');

    let isMember = sessionStorage.getItem('isMember');
    if (isMember) {
      sessionStorage.removeItem("merchantData");
      sessionStorage.removeItem('isMember')
      window.location.href = `${window.location.origin}/?${window.location.href.split('?')[1]}&logout=true`;
    } else {
      window.location.href =
        configs.authUrl + "/account/login?redirect=" + window.location.origin + "&logout=true";
    }
  };

  const planName = userData?.plan?.planName || "Your Plan";
  const pct =
    daysLeft == null
      ? 0
      : Math.max(0, Math.min(100, Math.round(((10 - daysLeft) / 10) * 100)));

  return (
    <Dialog
      open={open}
      onClose={isExpired ? undefined : handleCloseForToday}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          overflow: "hidden",
          borderRadius: 3,
          boxShadow: 10,
        },
      }}
    >
      {/* Header band */}
      <Box
        sx={{
          bgcolor: isExpired ? "#ffebee" : "#e3f2fd",
          borderBottom: "1px solid",
          borderColor: isExpired ? "#ffcdd2" : "#bbdefb",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        {isExpired ? (
          <EventBusyIcon color="error" />
        ) : (
          <LockClockIcon sx={{ color: themeColor }} />
        )}
        <Typography variant="h6" fontWeight={700}>
          {isExpired ? "Plan Expired" : "Plan Expiring Soon"}
        </Typography>
        <Chip
          label={planName}
          size="small"
          icon={<WorkspacePremiumIcon />}
          sx={{
            ml: "auto",
            bgcolor: themeColor,
            color: "#fff",
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={2}>
          {isExpired ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{ borderColor: "#ffcdd2" }}
            >
              Your subscription ended. Please renew to continue using all
              features.
            </Alert>
          ) : (
            <Alert severity="warning" variant="outlined">
              {daysLeft === 0 ? (
                <>
                  Your plan expires <b>today</b>.
                </>
              ) : (
                <>
                  Your plan will expire in <b>{daysLeft}</b> day
                  {daysLeft === 1 ? "" : "s"}.
                </>
              )}
            </Alert>
          )}

          <Box
            sx={{
              p: 2,
              border: "1px solid #eee",
              borderRadius: 2,
              background:
                "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.06))",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body2" fontWeight={600}>
                {isExpired ? "Expired" : "Time Remaining"}
              </Typography>
              {!isExpired && (
                <Typography variant="caption">
                  {Math.max(daysLeft, 0)} / 10 day window
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant={isExpired ? "determinate" : "determinate"}
              value={isExpired ? 100 : pct}
              sx={{
                height: 10,
                borderRadius: 5,
                "& .MuiLinearProgress-bar": { backgroundColor: themeColor },
              }}
            />
            <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
              <Chip
                size="small"
                label="POS"
                variant="outlined"
                sx={{ borderColor: themeColor, color: themeColor }}
              />
              <Chip
                size="small"
                label="Inventory"
                variant="outlined"
                sx={{ borderColor: themeColor, color: themeColor }}
              />
              <Chip
                size="small"
                label="Restaurant Solution"
                variant="outlined"
                sx={{ borderColor: themeColor, color: themeColor }}
              />
              <Chip
                size="small"
                label="Digital Signage"
                variant="outlined"
                sx={{ borderColor: themeColor, color: themeColor }}
              />
            </Stack>
          </Box>

          <Divider />

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Keep uninterrupted access to orders, POS, reports, and more. Renew
            now to avoid service disruption.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        <Button onClick={handleLogout} color="error">
          Logout
        </Button>
        {!isExpired && (
          <>
            <Button onClick={handleRemindTomorrow}>Remind Later</Button>
            <Button onClick={handleCloseForToday}>Close</Button>
          </>
        )}
        <Box sx={{ flexGrow: 1 }} />

        {!(userData?.isIAM) && <Button
          variant="contained"
          onClick={handleUpdatePlan}
          sx={{ bgcolor: themeColor, "&:hover": { bgcolor: themeColor } }}
        >
          Update Plan
        </Button>}
      </DialogActions>
    </Dialog>
  );
}

export default PlanExpiryDialog;
